import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  IconButton,
  Popper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import CustomYearDatePicker from '../CustomDates/CustomYearDatePicker/CustomYearDatePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Constants from '../../../assests/Constants/constants';
import Iframe from 'react-iframe';

dayjs.extend(utc);

function DocumentTypeYearModal({ open, onClose, onConfirm, direction, pendingFiles = [] }) {
  const { t } = useTranslation();
  const [documentType, setDocumentType] = useState(null);
  const [title, setTitle] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  // Initialize with end of current year in UTC (as CustomYearDatePicker expects)
  const [year, setYear] = useState(() => {
    return dayjs.utc().endOf('year').toDate();
  });

  // Get all documents for preview
  const allDocuments = useMemo(() => {
    return (pendingFiles || []).map((file, idx) => ({
      url: URL.createObjectURL(file),
      isExisting: false,
      id: `pending-${idx}`,
      name: file.name,
      file: file,
    }));
  }, [pendingFiles]);

  const currentDocument = allDocuments[currentDocIndex];
  const hasDocuments = allDocuments.length > 0;

  useEffect(() => {
    if (open) {
      // Reset state when modal opens
      setDocumentType(null);
      setTitle('');
      setCurrentDocIndex(0);
      setShowPreview(true);
      // Reset to end of current year in UTC
      setYear(dayjs.utc().endOf('year').toDate());
    }
  }, [open]);

  // Cleanup object URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      allDocuments.forEach(doc => {
        if (doc.url && doc.url.startsWith('blob:')) {
          URL.revokeObjectURL(doc.url);
        }
      });
    };
  }, [allDocuments]);

  const handleTogglePreview = () => {
    setShowPreview((prev) => !prev);
  };

  const handleNextDocument = () => {
    setCurrentDocIndex((prev) => (prev + 1) % allDocuments.length);
  };

  const handlePreviousDocument = () => {
    setCurrentDocIndex((prev) => (prev - 1 + allDocuments.length) % allDocuments.length);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleConfirm = () => {
    if (!documentType) {
      return; // Document type is required
    }

    // Get year from date (end of year date)
    const yearValue = dayjs.utc(year).year();
    const documentTypeValue = documentType.value || documentType;
    const documentTypeEnum = documentType.enumValue !== undefined ? documentType.enumValue : 
      Constants.AccountCardDocumentTypes.find(dt => dt.value === documentTypeValue)?.enumValue ?? 4; // Default to "Other" (4)
    
    // Generate filename: {documentType}_{year} (use title if provided, otherwise use default)
    const fileName = `${documentTypeValue}_${yearValue}`;
    const documentTitle = title || fileName;
    // Pass: fileName (for backward compatibility), year (integer), documentType (Hebrew string), documentTypeEnum (enum value), title (string)
    onConfirm(fileName, yearValue, documentTypeValue, documentTypeEnum, documentTitle);
    onClose();
  };

  const handleYearChange = (date) => {
    setYear(date);
  };

  const isConfirmDisabled = !documentType;

  const inputPropsStyle = {
    disableUnderline: true,
    className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
    sx: {
      height: '48px',
      padding: '0 10px',
      borderRadius: '8px',
      background: '#F5F5F6',
      border: '1px solid transparent',
      '&:focus-within': {
        border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
      },
    },
  };

  const inputStyle = {
    style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={hasDocuments && showPreview ? "lg" : "sm"}
      fullWidth
      sx={{
        zIndex: 10002,
        '& .MuiDialog-root': {
          zIndex: 10002,
        },
        '& .MuiBackdrop-root': {
          zIndex: 10002,
        },
        '& .MuiDialog-container': {
          zIndex: 10002,
        },
        // Ensure DatePicker popper appears above modal
        '& .MuiPickersPopper-root': {
          zIndex: 10005,
        },
      }}
      BackdropProps={{
        style: {
          zIndex: 10002,
          position: 'fixed',
        },
      }}
      PaperProps={{
        style: {
          zIndex: 10003,
          position: 'relative',
        },
      }}
      disableEnforceFocus
      disableRestoreFocus
    >
      <DialogTitle
        dir={direction}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>{t('SelectDocumentTypeAndYear') || 'בחר סוג מסמך ושנה'}</span>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ padding: '16px', overflow: 'hidden' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: hasDocuments && showPreview ? 'row' : 'column', 
          gap: 2, 
          width: '100%',
          alignItems: hasDocuments && showPreview ? 'stretch' : 'center'
        }}>
          {/* Document Preview - Right Side */}
          {hasDocuments && showPreview && currentDocument && (
            <Box 
              dir={direction}
              sx={{ 
                flex: "0 0 50%",
                marginLeft: 2,
                minHeight: "600px",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {/* Document Navigation - Left arrow */}
              {currentDocIndex > 0 && (
                <div
                  onClick={handlePreviousDocument}
                  style={{
                    cursor: "pointer",
                    padding: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#304FFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "absolute",
                    left: "20px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#1E40FF";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#304FFF";
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M14.9995 15.875L11.1195 11.995L14.9995 8.11498C15.3895 7.72498 15.3895 7.09498 14.9995 6.70498C14.6095 6.31498 13.9795 6.31498 13.5895 6.70498L8.99953 11.295C8.60953 11.685 8.60953 12.315 8.99953 12.705L13.5895 17.295C13.9795 17.685 14.6095 17.685 14.9995 17.295C15.3795 16.905 15.3895 16.265 14.9995 15.875Z"
                      fill="white"
                    />
                  </svg>
                </div>
              )}

              {/* Document counter */}
              {allDocuments.length > 1 && (
                <div
                  style={{
                    position: "absolute",
                    top: "50px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 10,
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    padding: "8px 16px",
                    borderRadius: "20px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#304FFF",
                    minWidth: "80px",
                    textAlign: "center",
                  }}
                >
                  {t("Document")} {currentDocIndex + 1} {t("of")} {allDocuments.length}
                </div>
              )}

              {/* Document Navigation - Right arrow */}
              {currentDocIndex < allDocuments.length - 1 && (
                <div
                  onClick={handleNextDocument}
                  style={{
                    cursor: "pointer",
                    padding: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#304FFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "absolute",
                    right: "20px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#1E40FF";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#304FFF";
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M9.00047 15.875L12.8805 11.995L9.00047 8.11498C8.61047 7.72498 8.61047 7.09498 9.00047 6.70498C9.39047 6.31498 10.0205 6.31498 10.4105 6.70498L15.0005 11.295C15.3905 11.685 15.3905 12.315 15.0005 12.705L10.4105 17.295C10.0205 17.685 9.39047 17.685 9.00047 17.295C8.62047 16.905 8.61047 16.265 9.00047 15.875Z"
                      fill="white"
                    />
                  </svg>
                </div>
              )}

              {/* Document Display - Iframe */}
              <Iframe
                url={`${currentDocument.url}#page=1&view=FitH`}
                width="100%"
                height="100%"
                id="document-type-year-preview"
                position="relative"
                title={currentDocument.name}
                allowFullScreen
              />
            </Box>
          )}
        <Box dir={direction} sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minWidth: hasDocuments && showPreview ? '400px' : 'auto'
          }}>
            {/* Selected Documents List */}
            {pendingFiles.length > 0 && (
              <Box dir={direction} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: "#304FFF", 
                      display: "block",
                      fontWeight: 600,
                      fontSize: "12px",
                    }}
                  >
                    {t("SelectedDocuments") || "מסמכים נבחרים"} ({pendingFiles.length})
                  </Typography>
                  <IconButton onClick={handleTogglePreview} size="small" sx={{ padding: '4px' }}>
                    {showPreview ? <VisibilityOffIcon sx={{ color: "#304FFF", fontSize: 20 }} /> : <VisibilityIcon sx={{ color: "#304FFF", fontSize: 20 }} />}
                  </IconButton>
                </Box>
                {pendingFiles.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1,
                      py: 0.75,
                      mb: 0.75,
                      borderRadius: 1.5,
                      background: "linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)",
                      border: "1px solid #2196F3",
                      minHeight: "40px",
                      animation: "slideIn 0.3s ease-out",
                      "@keyframes slideIn": {
                        "0%": {
                          opacity: 0,
                          transform: "translateX(-20px)",
                        },
                        "100%": {
                          opacity: 1,
                          transform: "translateX(0)",
                        },
                      },
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateX(4px)",
                        boxShadow: "0 2px 8px rgba(33, 150, 243, 0.2)",
                      },
                    }}
                  >
                    <InsertDriveFileIcon sx={{ color: "#1976D2", fontSize: 20 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 600,
                          fontSize: "10px",
                          lineHeight: "1.3",
                          display: "block",
                          wordBreak: "break-all",
                          color: "#1565C0",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {file.name}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: "#1976D2",
                          cursor: "pointer",
                          textDecoration: "underline",
                          display: "block",
                          mt: 0.25,
                          fontSize: "9px",
                          fontWeight: 500,
                          "&:hover": {
                            color: "#0D47A1",
                          },
                        }}
                        onClick={() => {
                          setCurrentDocIndex(index);
                          setShowPreview(true);
                        }}
                      >
                        {formatFileSize(file.size)} • {t("ViewDocument") || "View"}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Title Field */}
            <Box>
              <div
                className="edit-form-row-item-title"
                style={{ marginBottom: '8px' }}
              >
                {t('Title') || 'כותרת'}
              </div>
              <TextField
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                variant="standard"
                placeholder={t('EnterDocumentTitle') || 'הכנס כותרת מסמך (אופציונלי)'}
                InputProps={inputPropsStyle}
                inputProps={inputStyle}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'center', mt: 2}}>
              {/* Year Picker */}
              <Box sx={{ flex: 1 }}>
                <div
                  className="edit-form-row-item-title"
                  style={{ marginBottom: '8px' }}
                >
                  {t('Year') || 'שנה'}
                </div>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <CustomYearDatePicker
                    date={year}
                    label={t('Year') || 'שנה'}
                    onDateChange={handleYearChange}
                    height="48px"
                  />
                </Box>
              </Box>

              {/* Document Type */}
              <Box sx={{ flex: 1 }}>
                <div
                  className="edit-form-row-item-title"
                  style={{ marginBottom: '8px' }}
                >
                  {t('DocumentType') || 'סוג מסמך'}
                  <span className="required-asterisk">*</span>
                </div>
                <Autocomplete
                  options={Constants.AccountCardDocumentTypes}
                  getOptionLabel={(option) => option.label || option}
                  value={documentType}
                  onChange={(_, newValue) => setDocumentType(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder={t('SelectDocumentType') || 'בחר סוג מסמך'}
                      InputProps={{
                        ...params.InputProps,
                        ...inputPropsStyle,
                      }}
                      inputProps={{ ...params.inputProps, ...inputStyle }}
                    />
                  )}
                  PopperComponent={(props) => (
                    <Popper
                      {...props}
                      style={{
                        ...props.style,
                        direction,
                        zIndex: 10004,
                      }}
                    />
                  )}
                  PopperProps={{
                    style: {
                      zIndex: 10004,
                    },
                    placement: direction === 'rtl' ? 'bottom-end' : 'bottom-start',
                  }}
                  disablePortal={false}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '16px', direction }}>
        <Button onClick={onClose} variant="outlined">
          {t('Cancel') || 'ביטול'}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isConfirmDisabled}
          sx={{
            backgroundColor: '#304FFF',
            '&:hover': {
              backgroundColor: '#1E40FF',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#666',
            },
          }}
        >
          {t('Confirm') || 'אישור'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DocumentTypeYearModal;

